import React, {useEffect, useState, useRef, useMemo, useCallback} from 'react';
import {useLocation} from "react-router-dom";
import {useTabHashPersistence} from "../../../../hooks/useTabHashPersistence";
import {useWeekStartsSunday} from "../../../../hooks/useWeekStartsSunday";
import {CustomCard} from "../../../../components/UI/AntCustom/CustomCard";
import {PageWrapper} from "../../../../components/UI/PageWrapper/PageWrapper";
import {useAppDispatch, useAppSelector} from "../../../../store/store";
import {
  getEventCalendarList,
  clearEventCalendarList,
} from "../../../../store/reducers/eventCalendarReducer/eventCalendarReducer";
import {getPlaneList} from "../../../../store/reducers/fleetReducer/fleetReducer";
import {IEventCalendar} from "../../../../store/reducers/eventCalendarReducer/eventCalendarReducerTypes";
import {IPlaneShort} from "../../../../store/reducers/fleetReducer/fleetReducerTypes";
import {InitialPage} from "../../../../utils/constans/pagination/pagination";
import {CustomButton} from "../../../../components/UI/AntCustom/CustomButton";
import {CustomTabs} from "../../../../components/UI/AntCustom/CustomTabs";
import {Switch} from "antd";
import styled from "styled-components";
import {defineMessages, useIntl} from "react-intl";
import {FullCalendarWrapper} from "../Schedule/components/FullCalendarWrapper";
import {EventCalendarCreateModal} from "./components/EventCalendarCreateModal";
import {EventCalendarDeleteModal} from "./components/EventCalendarDeleteModal";
import {EventCalendarTimeline} from "./components/EventCalendarTimeline";
import {EventCalendarSimpleFilters} from "./components/EventCalendarSimpleFilters";
import CalendarIcon from "../../../../media/icons/calendar.svg?react";
import CalendarBlackIcon from "../../../../media/icons/calendar_black.svg?react";
import TimelineIcon from "../../../../media/icons/timeline.svg?react";
import TimelineBlackIcon from "../../../../media/icons/timeline_black.svg?react";
import {Role} from "../../../../config/acl/roles/Role";
import {useIsMobile} from "../../../../hooks/useWindowSize";

const ControlsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FiltersAndActionsRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
`;

const FiltersWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  flex: 1;
  min-width: 0;
`;


const EventCalendarPageMessages = defineMessages({
  title: {
    id: "eventCalendarPage.title",
    defaultMessage: "Календарь событий",
  },
  description: {
    id: "eventCalendarPage.description",
    defaultMessage: "Добавляя ивенты в эти календаря это влияет стоимость и коэффициент авиаперелета",
  },
  add: {
    id: "eventCalendarPage.add",
    defaultMessage: "Add",
  },
  addEvent: {
    id: "eventCalendarPage.addEvent",
    defaultMessage: "Добавить событие",
  },
  events: {
    id: "eventCalendarPage.events",
    defaultMessage: "События",
  },
  seasons: {
    id: "eventCalendarPage.seasons",
    defaultMessage: "Сезоны",
  },
  weekFromSunday: {
    id: "eventCalendarPage.weekFromSunday",
    defaultMessage: "Week starts on Sunday",
  },
  calendar: {
    id: "eventCalendarPage.calendar",
    defaultMessage: "Календарь",
  },
  timeline: {
    id: "eventCalendarPage.timeline",
    defaultMessage: "Таймлайн",
  },
  month: {
    id: "eventCalendarPage.month",
    defaultMessage: "Месяц",
  },
  year: {
    id: "eventCalendarPage.year",
    defaultMessage: "Год",
  },
  week: {
    id: "eventCalendarPage.week",
    defaultMessage: "Неделя",
  },
});

const TabLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const TabIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const WeekToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #666666;
`;

const TabsAndFiltersRow = styled.div`
  margin-bottom: 8px;
  
  .ant-tabs-nav {
    margin-bottom: 8px;
  }
  
  .ant-tabs-nav-wrap {
    height: fit-content !important;
    align-self: flex-end !important;
  }
  
  .ant-tabs-nav-list {
    align-items: flex-start !important;
  }
  
  .ant-tabs-extra-content {
    margin-bottom: 5px;
    align-self: flex-start !important;
  }
  
  .ant-tabs-tab {
    padding: 8px 12px;
    margin: 0 0 0 8px;
    align-self: flex-start !important;
  }
  
  .ant-tabs-tab-btn {
    padding: 0;
  }

  @media (max-width: 768px) {
    display: flex !important;
    flex-direction: column !important;
    gap: 16px !important;
  }
`;

const FiltersRightWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DesktopFiltersWrapper = styled(FiltersRightWrapper)`
  @media (max-width: 768px) {
    display: none !important;
  }
`;

const MobileFiltersWrapper = styled.div`
  @media (min-width: 769px) {
    display: none !important;
  }
  
  @media (max-width: 768px) {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: wrap !important;
    width: 100% !important;
    order: 1;
    margin-bottom: 16px;
    gap: 16px;
  }
`;

const TabsWrapper = styled.div`
  @media (max-width: 768px) {
    order: 2;
    width: 100%;
  }
`;

const StyledCustomCard = styled(CustomCard)`
  @media (max-width: 768px) {
    .ant-card-head-wrapper {
      flex-wrap: wrap;
      gap: 8px;
    }
    
    .ant-card-head-title {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    
    .ant-card-extra {
      flex-shrink: 0;
      margin-left: auto;
    }
  }
`;

const TABS = {
  calendar: "calendar",
  timeline: "timeline",
  map: "map",
} as const;

type TabKey = typeof TABS[keyof typeof TABS];

export const EventCalendarPage = () => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const isMobile = useIsMobile();
  const location = useLocation();
  const {items, isLoading, totalCount} = useAppSelector(state => state.eventCalendar.eventCalendarList);
  
  // Определяем роль пользователя для фильтрации событий
  const userRole = useAppSelector(state => state.auth.role);
  const isSystemAdmin = userRole === Role.admin;
  
  // Отслеживаем предыдущий путь для определения ухода со страницы
  const prevLocationRef = useRef<string>('');
  const isMountedRef = useRef<boolean>(false);
  // Ключ для календаря, чтобы пересоздавать его при очистке
  const [calendarKey, setCalendarKey] = useState<number>(0);

  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [lastSelectedEvent, setLastSelectedEvent] = useState<IEventCalendar | undefined>(undefined);
  const [duplicateEvent, setDuplicateEvent] = useState<IEventCalendar | undefined>(undefined);
  // Добавляем ключ для модалки, чтобы она полностью пересоздавалась при изменении режима
  const [modalKey, setModalKey] = useState(0);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [visibleDateRange, setVisibleDateRange] = useState<{start: Date, end: Date} | null>(null);
  const [weekStartsSunday, setWeekStartsSunday] = useWeekStartsSunday();
  const [activeTabKey, setActiveTabKey] = useTabHashPersistence(TABS, TABS.calendar);
  
  const [filters, setFilters] = useState<{
    airportIds?: number[];
  }>({});

  // Используем useRef для отслеживания предыдущих значений и предотвращения лишних вызовов
  const prevParamsRef = useRef<{
    dateFrom?: string;
    dateTo?: string;
    planeTypeId?: number;
    hasSeasonType?: boolean;
  } | null>(null);

  // Флаг для блокировки повторных вызовов
  const isRequestInProgressRef = useRef(false);

  // Мемоизируем параметры запроса
  const requestParams = useMemo(() => {
    if (!visibleDateRange) {
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      
      return {
        dateFrom: startOfMonth.toISOString().split('T')[0],
        dateTo: endOfMonth.toISOString().split('T')[0],
        planeTypeId: undefined,
        hasSeasonType: false,
      };
    } else {
      return {
        dateFrom: visibleDateRange.start.toISOString().split('T')[0],
        dateTo: visibleDateRange.end.toISOString().split('T')[0],
        planeTypeId: undefined,
        hasSeasonType: false,
      };
    }
  }, [currentDate, visibleDateRange]);

  // Загружаем данные при изменении фильтров или видимого диапазона дат
  useEffect(() => {
    // Блокируем повторные вызовы
    if (isRequestInProgressRef.current) {
      return;
    }
    
    // Создаем строковое представление текущих параметров для сравнения
    const currentParamsStr = JSON.stringify({
      dateFrom: requestParams.dateFrom,
      dateTo: requestParams.dateTo,
      planeTypeId: requestParams.planeTypeId,
      hasSeasonType: requestParams.hasSeasonType,
    });
    
    const prevParamsStr = prevParamsRef.current 
      ? JSON.stringify(prevParamsRef.current)
      : null;
    
    // Проверяем, изменились ли параметры запроса
    const paramsChanged = prevParamsStr !== currentParamsStr;

    // Выполняем запрос только если параметры изменились
    if (paramsChanged) {
      isRequestInProgressRef.current = true;
      
      dispatch(getEventCalendarList({
        pageModel: { page: InitialPage, limit: 1000 },
        dateFrom: requestParams.dateFrom,
        dateTo: requestParams.dateTo,
        planeTypeId: requestParams.planeTypeId,
        hasSeasonType: requestParams.hasSeasonType,
      }));
      
      prevParamsRef.current = { ...requestParams };
      
      // Снимаем блокировку через небольшую задержку
      setTimeout(() => {
        isRequestInProgressRef.current = false;
      }, 100);
    }
  }, [requestParams.dateFrom, requestParams.dateTo, requestParams.planeTypeId, requestParams.hasSeasonType, dispatch])

  // Отслеживаем уход со страницы через изменение location и очищаем календарь
  useEffect(() => {
    const currentPath = location.pathname;
    // Проверяем, находимся ли мы на странице Event Calendar
    const isEventCalendarPage = currentPath === '/lk/event-calendar' || 
                                 currentPath === '/event-calendar' ||
                                 currentPath.startsWith('/lk/event-calendar') ||
                                 currentPath.startsWith('/event-calendar');
    const wasEventCalendarPage = prevLocationRef.current === '/lk/event-calendar' || 
                                  prevLocationRef.current === '/event-calendar' ||
                                  prevLocationRef.current.startsWith('/lk/event-calendar') ||
                                  prevLocationRef.current.startsWith('/event-calendar');
    
    // При первом монтировании компонента очищаем календарь
    if (!isMountedRef.current) {
      if (isEventCalendarPage) {
        dispatch(clearEventCalendarList());
        setCalendarKey(prev => prev + 1); // Пересоздаем календарь
        isMountedRef.current = true;
      }
    } else {
      // Если ушли со страницы Event Calendar, очищаем календарь
      if (wasEventCalendarPage && !isEventCalendarPage) {
        dispatch(clearEventCalendarList());
        setCalendarKey(prev => prev + 1); // Пересоздаем календарь
      }
      
      // Если зашли на страницу Event Calendar (после ухода), очищаем календарь для чистого старта
      if (isEventCalendarPage && !wasEventCalendarPage) {
        dispatch(clearEventCalendarList());
        setCalendarKey(prev => prev + 1); // Пересоздаем календарь
      }
    }
    
    prevLocationRef.current = currentPath;
  }, [location.pathname, dispatch]);

  // Загружаем самолеты оператора при загрузке страницы (только для операторов)
  useEffect(() => {
    if (!isSystemAdmin) {
      dispatch(getPlaneList({
        query: '',
        page: InitialPage
      }));
    }
  }, [dispatch, isSystemAdmin]);

  // Очищаем список при размонтировании компонента
  useEffect(() => {
    return () => {
      dispatch(clearEventCalendarList());
      isMountedRef.current = false;
    }
  }, [dispatch])

  // Функция для обновления списка событий после создания/редактирования
  const handleRefreshEventList = useCallback(() => {
    const params = requestParams;
    dispatch(getEventCalendarList({
      pageModel: { page: InitialPage, limit: 1000 },
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
      planeTypeId: params.planeTypeId,
      hasSeasonType: params.hasSeasonType,
    }));
  }, [requestParams, dispatch])

  // Проверяем пересечения событий для визуальных индикаторов
  // Учитывает блоки с несколькими аэропортами и классы самолетов (Light/Midsize)
  const checkEventOverlap = (event: IEventCalendar): {hasOverlap: boolean; overlappingEvents: IEventCalendar[]} => {
    if (event.source !== 'operator_admin') {
      return {hasOverlap: false, overlappingEvents: []};
    }

    // Парсим даты: если приходит только дата без времени, добавляем время
    const parseDate = (dateString: string): Date => {
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return new Date(dateString + 'T00:00:00');
      }
      return new Date(dateString);
    };

    const overlappingEvents: IEventCalendar[] = [];
    const eventDateFrom = parseDate(event.dateFrom);
    const eventDateTo = parseDate(event.dateTo);

    // Получаем все аэропорты из блоков события оператора
    const operatorAirportIds = new Set<number>();
    if (event.blocks && event.blocks.length > 0) {
      event.blocks.forEach(block => {
        if (block.airportIds && block.airportIds.length > 0) {
          block.airportIds.forEach(airportId => operatorAirportIds.add(airportId));
        }
      });
    }

    // Получаем классы самолетов из блоков события оператора (Light/Midsize)
    const operatorHasLight = event.blocks?.some(block => block.priceLight !== undefined) ?? false;
    const operatorHasMidsize = event.blocks?.some(block => block.priceMidsize !== undefined) ?? false;

    // Проверяем пересечения с системными событиями
    items.forEach((systemEvent: IEventCalendar) => {
      if (
        systemEvent.source === 'system' &&
        systemEvent.eventCalendarId !== event.eventCalendarId &&
        true // isActive removed
      ) {
        const systemDateFrom = parseDate(systemEvent.dateFrom);
        const systemDateTo = parseDate(systemEvent.dateTo);

        // Проверяем пересечение по датам
        const datesOverlap = systemDateFrom <= eventDateTo && systemDateTo >= eventDateFrom;
        if (!datesOverlap) {
          return;
        }

        // Проверяем пересечение по аэропортам
        let airportsOverlap = false;
        if (systemEvent.blocks && systemEvent.blocks.length > 0) {
          // Системное событие с блоками
          systemEvent.blocks.forEach((block) => {
            if (block.airportIds && block.airportIds.length > 0) {
              block.airportIds.forEach((airportId: number) => {
                if (operatorAirportIds.has(airportId) || operatorAirportIds.size === 0) {
                  airportsOverlap = true;
                }
              });
            } else if (operatorAirportIds.size === 0) {
              // Блок без аэропортов = для всех аэропортов страны
              airportsOverlap = true;
            }
          });
        } else {
          // Системное событие для всех аэропортов страны
          airportsOverlap = true;
        }

        if (!airportsOverlap) {
          return;
        }

        // Проверяем пересечение по классам самолетов
        let planeClassesOverlap = false;
        if (systemEvent.blocks && systemEvent.blocks.length > 0) {
          // Системное событие с блоками
          const systemHasLight = systemEvent.blocks.some((block: {priceLight?: number}) => block.priceLight !== undefined);
          const systemHasMidsize = systemEvent.blocks.some((block: {priceMidsize?: number}) => block.priceMidsize !== undefined);
          
          if ((operatorHasLight && systemHasLight) || (operatorHasMidsize && systemHasMidsize)) {
            planeClassesOverlap = true;
          }
        } else {
          // Системное событие без блоков (старая структура) - считаем, что пересекается со всеми классами
          planeClassesOverlap = true;
        }

        if (planeClassesOverlap) {
          overlappingEvents.push(systemEvent);
        }
      }
    });

    return {
      hasOverlap: overlappingEvents.length > 0,
      overlappingEvents,
    };
  };

  // Парсим даты: если приходит только дата без времени, добавляем время
  // ВАЖНО: даты приходят в UTC-0 и должны остаться в UTC-0, не конвертируем в локальный часовой пояс
  const parseEventDate = useCallback((dateString: string): Date => {
    // Если дата в формате "YYYY-MM-DD" без времени, добавляем время 00:00:00Z (UTC)
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // Добавляем 'Z' в конец, чтобы указать, что это UTC время
      return new Date(dateString + 'T00:00:00Z');
    }
    // Если есть время, проверяем, есть ли уже указание на UTC (Z в конце)
    // Если нет Z, добавляем его, чтобы дата парсилась как UTC
    if (!dateString.endsWith('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
      // Если строка содержит время, но нет указания на часовой пояс, добавляем Z
      // Проверяем формат "YYYY-MM-DD HH:mm:ss" или "YYYY-MM-DDTHH:mm:ss"
      if (dateString.match(/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}$/)) {
        return new Date(dateString.replace(' ', 'T') + 'Z');
      }
    }
    // Если уже есть Z или другой указатель часового пояса, парсим как есть
    return new Date(dateString);
  }, []);

  // Фильтруем события по выбранным аэропортам
  const filteredItems = useMemo(() => {
    if (!filters.airportIds || filters.airportIds.length === 0) {
      return items;
    }
    
    return items.filter((event: IEventCalendar) => {
      // Если у события есть блоки, проверяем их
      if (event.blocks && event.blocks.length > 0) {
        // Проверяем, есть ли хотя бы один блок, который соответствует фильтру
        return event.blocks.some((block) => {
          // Если у блока airportIds null или пустой массив, он применяется ко всем аэропортам
          if (!block.airportIds || block.airportIds.length === 0) {
            return true;
          }
          // Проверяем, есть ли пересечение между airportIds блока и фильтром
          return block.airportIds.some((airportId) => filters.airportIds!.includes(airportId));
        });
      }
      // Если у события нет блоков, оно не соответствует фильтру (не должно отображаться)
      return false;
    });
  }, [items, filters]);

  // Преобразуем события Event Calendar в формат для FullCalendar
  const calendarEvents = useMemo(() => {
    return filteredItems.map((event: IEventCalendar) => {
      // Создаем фиктивные значения для совместимости с IPlaneEvent
      const dummyPlane: IPlaneShort = {
        id: 0,
        registrationNumber: '',
      };
      const dummyAirport = {
        id: 0,
        name: '',
        icaoCode: '',
        iataCode: '',
        countryName: '',
        timezone: {
          id: 0,
          name: '',
          offset: 0,
        },
      };
      
      const startDate = parseEventDate(event.dateFrom);
      const endDate = parseEventDate(event.dateTo);
      
      // Для FullCalendar end должен быть эксклюзивным (следующий день после окончания)
      const endDateExclusive = new Date(endDate);
      endDateExclusive.setDate(endDateExclusive.getDate() + 1);
      
      const overlapResult = checkEventOverlap(event);
      const hasOverlap = overlapResult.hasOverlap;
      const isPriority = event.source === 'operator_admin' && hasOverlap;
      
      // Используем цвет из события, если он есть, иначе используем дефолтные цвета
      const eventColor = event.color || (isPriority ? '#52C41A' : event.source === 'system' ? '#1890FF' : '#722ED1');
      
      // Преобразуем hex в rgba с прозрачностью 0.5 для фона (50% opacity)
      const hex = eventColor.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      const backgroundColor = `rgba(${r}, ${g}, ${b}, 0.5)`;
      const borderColor = eventColor;

      const calendarEvent = {
        id: event.eventCalendarId,
        title: event.eventName, // Просто eventName без звездочки, так как цвет уже показывает приоритет
        start: startDate,
        end: endDateExclusive,
        eventType: 0, // Для совместимости с IPlaneEvent
        plane: dummyPlane,
        dateFrom: startDate,
        dateTo: endDate,
        airportFrom: dummyAirport,
        airportTo: undefined,
        eventName: event.eventName,
        extendedProps: {
          event: event, // Передаем полный объект события со всеми полями включая blocks
          hasOverlap,
          isPriority,
          overlappingEvents: overlapResult.overlappingEvents,
        },
        backgroundColor: backgroundColor,
        borderColor: borderColor,
      };
      
      return calendarEvent;
    });
  }, [filteredItems, parseEventDate]);

  const handleEventClick = (event: IEventCalendar | any) => {
    // Берем только ID события
    const eventId = event?.eventCalendarId || event?.id;
    
    if (!eventId) {
      console.error('No event ID found:', event);
      return;
    }
    
    // Находим ПОЛНЫЙ объект события из items по ID (используем items, а не filteredItems, чтобы найти событие даже если оно отфильтровано)
    const fullEvent = items.find((e: IEventCalendar) => e.eventCalendarId === eventId);
    
    if (!fullEvent) {
      console.error('Event not found in items:', eventId, 'Available items:', items.map(e => e.eventCalendarId));
      return;
    }
    
    // Передаем ПОЛНЫЙ объект из items в модалку и обновляем ключ модалки
    setLastSelectedEvent(fullEvent);
    setModalKey(prev => prev + 1);
    setIsCreateModalVisible(true);
  };

  const handleDateSelect = (start: Date, end: Date) => {
    // Можно добавить логику для создания события на выбранную дату
  };

  const handleDatesChange = useCallback((start: Date, end: Date) => {
    // Проверяем, изменились ли даты, чтобы избежать лишних обновлений
    const startStr = start.toISOString().split('T')[0];
    const endStr = end.toISOString().split('T')[0];
    
    // Если visibleDateRange еще не установлен, проверяем, совпадают ли даты с текущим месяцем
    if (!visibleDateRange) {
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      const currentMonthStartStr = startOfMonth.toISOString().split('T')[0];
      const currentMonthEndStr = endOfMonth.toISOString().split('T')[0];
      
      // Если даты совпадают с текущим месяцем, не обновляем visibleDateRange
      // Это предотвращает лишний вызов API при первой инициализации календаря
      if (startStr === currentMonthStartStr && endStr === currentMonthEndStr) {
        return;
      }
    }
    
    // Проверяем, действительно ли даты изменились
    if (!visibleDateRange || 
        visibleDateRange.start.toISOString().split('T')[0] !== startStr ||
        visibleDateRange.end.toISOString().split('T')[0] !== endStr) {
      setVisibleDateRange({ start, end });
    }
  }, [currentDate, visibleDateRange]);

  const renderCalendarContent = () => {
    return (
      <FullCalendarWrapper
        key={calendarKey}
        events={calendarEvents}
        onEventClick={handleEventClick}
        onDateSelect={handleDateSelect}
        onDatesChange={handleDatesChange}
        height={600}
        initialView="dayGridMonth"
        initialDate={currentDate}
        firstDayOfWeek={weekStartsSunday ? 0 : 1}
        headerRightContent={
          <WeekToggleWrapper>
            <Switch
              checked={weekStartsSunday}
              onChange={setWeekStartsSunday}
              size="small"
            />
            <span>{intl.formatMessage(EventCalendarPageMessages.weekFromSunday)}</span>
          </WeekToggleWrapper>
        }
      />
    );
  };

  const renderTimelineContent = () => {
    const currentYear = currentDate.getFullYear();

    return (
      <EventCalendarTimeline
        events={filteredItems}
        onEventClick={(event) => {
          setLastSelectedEvent(event);
          setModalKey(prev => prev + 1);
          setIsCreateModalVisible(true);
        }}
        currentYear={currentYear}
      />
    );
  };

  const addEventButton = (
    <CustomButton
      heightSize={isMobile ? 's' : 'm'}
      type={'primary'}
      onClick={() => {
        setLastSelectedEvent(undefined);
        setModalKey(prev => prev + 1);
        setIsCreateModalVisible(true);
      }}
    >
      + {isMobile ? intl.formatMessage(EventCalendarPageMessages.add) : intl.formatMessage(EventCalendarPageMessages.addEvent)}
    </CustomButton>
  );

  return (
    <PageWrapper>
      <StyledCustomCard width={'max'} title={intl.formatMessage(EventCalendarPageMessages.title)} isContainerCard={true}
                        extra={addEventButton} titlePosition={'space-between'}>
        <TabsAndFiltersRow>
          <MobileFiltersWrapper>
            <EventCalendarSimpleFilters filters={filters} onFiltersChange={setFilters} />
          </MobileFiltersWrapper>
          <TabsWrapper>
            <CustomTabs 
              activeKey={activeTabKey} 
              onChange={(key) => setActiveTabKey(key as TabKey)} 
              destroyInactiveTabPane
              tabBarExtraContent={
                <DesktopFiltersWrapper>
                  <EventCalendarSimpleFilters filters={filters} onFiltersChange={setFilters} />
                </DesktopFiltersWrapper>
              }
              items={[
                {
                  key: TABS.calendar,
                  label: (
                    <TabLabel>
                      <TabIconWrapper>
                        {activeTabKey === TABS.calendar ? <CalendarIcon /> : <CalendarBlackIcon />}
                      </TabIconWrapper>
                      {intl.formatMessage(EventCalendarPageMessages.calendar)}
                    </TabLabel>
                  ),
                  children: renderCalendarContent(),
                },
                {
                  key: TABS.timeline,
                  label: (
                    <TabLabel>
                      <TabIconWrapper>
                        {activeTabKey === TABS.timeline ? <TimelineIcon /> : <TimelineBlackIcon />}
                      </TabIconWrapper>
                      {intl.formatMessage(EventCalendarPageMessages.timeline)}
                    </TabLabel>
                  ),
                  children: renderTimelineContent(),
                },
              ]}
            />
          </TabsWrapper>
        </TabsAndFiltersRow>
      </StyledCustomCard>

      <EventCalendarCreateModal 
        key={modalKey}
        isModalVisible={isCreateModalVisible} 
        setIsModalVisible={(visible) => {
          setIsCreateModalVisible(visible);
          // Очищаем выбранное событие при закрытии модалки
          if (!visible) {
            setLastSelectedEvent(undefined);
            setDuplicateEvent(undefined);
          }
        }}
        isSeason={false}
        duplicateFrom={duplicateEvent || (!lastSelectedEvent ? undefined : lastSelectedEvent)}
        editEvent={lastSelectedEvent}
        onDelete={() => {
          // Открываем модалку удаления, модалка создания остается открытой
          setIsDeleteModalVisible(true);
        }}
        onDuplicate={() => {
          if (lastSelectedEvent) {
            // Создаем копию события с измененным именем
            const duplicatedEvent: IEventCalendar = {
              ...lastSelectedEvent,
              eventName: lastSelectedEvent.eventName.endsWith(':copy') 
                ? lastSelectedEvent.eventName 
                : `${lastSelectedEvent.eventName}:copy`,
            };
            // Закрываем модалку редактирования
            setIsCreateModalVisible(false);
            setLastSelectedEvent(undefined);
            // Открываем новую модалку создания с дублированным событием
            setDuplicateEvent(duplicatedEvent);
            setModalKey(prev => prev + 1);
            setIsCreateModalVisible(true);
          }
        }}
        onSuccess={() => {
          // Очищаем выбранное событие и обновляем список
          setLastSelectedEvent(undefined);
          setDuplicateEvent(undefined);
          setIsCreateModalVisible(false);
          handleRefreshEventList();
        }}
      />
      {lastSelectedEvent && (
        <EventCalendarDeleteModal 
          eventCalendar={lastSelectedEvent} 
          isModalVisible={isDeleteModalVisible}
          setIsModalVisible={setIsDeleteModalVisible}
          onSuccess={() => {
            // Закрываем модалку создания после успешного удаления
            setIsCreateModalVisible(false);
            setIsDeleteModalVisible(false);
            setLastSelectedEvent(undefined);
            setModalKey(prev => prev + 1);
            // Обновляем список событий на календаре
            handleRefreshEventList();
          }}
        />
      )}
    </PageWrapper>
  );
};

